import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Typography,
    Modal,
    Input,
    Select,
    message,
    Badge,
    Descriptions,
    Timeline,
    Divider,
    Empty,
    Row,
    Col,
    Tooltip,
    Dropdown,
    Menu
} from 'antd';
import {
    PlusOutlined,
    EyeOutlined,
    CloseOutlined,
    SearchOutlined,
    FilterOutlined,
    ExclamationCircleOutlined,
    ClearOutlined,
    MoreOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import disputeService from '../services/disputeService';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/Common/PageHeader';
import FileDisputeModal from '../components/Dispute/FileDisputeModal';
import LoginRequired from '../components/Common/LoginRequired';
import { toTitleCase } from '../utils/format';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Disputes = () => {
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fileModalVisible, setFileModalVisible] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const [filters, setFilters] = useState({
        status: null,
        type: null,
        search: ''
    });
    const [activeFilters, setActiveFilters] = useState({
        status: null,
        type: null,
        search: ''
    });
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
        loadDisputes();
        } else {
            // If user is not logged in, clear disputes and reset pagination
            setDisputes([]);
            setPagination(prev => ({ ...prev, total: 0 }));
            setLoading(false);
        }
    }, [activeFilters, pagination.current, currentUser]);

    const loadDisputes = async () => {
        if (!currentUser) {
            return;
        }
        try {
            setLoading(true);
            const params = {
                page: pagination.current,
                per_page: pagination.pageSize,
                ...activeFilters
            };
            const response = await disputeService.getDisputes(params);
            if (response.status === 'success') {
                setDisputes(response.data);
                if (response.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.pagination.total
                    }));
                }
            }
        } catch (error) {
            // Only show error if user is logged in
            if (currentUser) {
            message.error('Failed to load disputes');
            }
        } finally {
            setLoading(false);
        }
    };

    // Reset pagination when active filters change
    useEffect(() => {
        if (pagination.current !== 1) {
            setPagination(prev => ({
                ...prev,
                current: 1
            }));
        }
    }, [activeFilters]);

    // Handle search button click - apply all filters
    const handleSearch = () => {
        setActiveFilters({
            status: statusFilter,
            type: typeFilter,
            search: searchText.trim()
        });
        // Reset to first page
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle Enter key in search input
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear all filters and reset search
    const handleClearFilters = () => {
        setSearchText('');
        setStatusFilter(null);
        setTypeFilter(null);
        setActiveFilters({
            status: null,
            type: null,
            search: ''
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Check if there are active filters applied
    const hasActiveFilters = activeFilters.search || activeFilters.status || activeFilters.type;
    
    // Check if filters have changed from active ones (to show that search is needed)
    const hasPendingChanges = searchText !== activeFilters.search || 
                              statusFilter !== activeFilters.status || 
                              typeFilter !== activeFilters.type;

    const handleViewDispute = (dispute) => {
        navigate(`/disputes/${dispute.dispute_number.toLowerCase()}`);
    };

    const handleCancelDispute = async (dispute) => {
        Modal.confirm({
            title: 'Cancel Dispute',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to cancel this dispute? This action cannot be undone.',
            okText: 'Yes, Cancel Dispute',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const response = await disputeService.cancelDispute(dispute.dispute_number);
                    if (response.status === 'success') {
                        message.success('Dispute cancelled successfully');
                        loadDisputes();
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || 'Failed to cancel dispute');
                }
            }
        });
    };

    const handleFileDisputeClick = () => {
        if (!currentUser) {
            Modal.confirm({
                title: 'Login Required',
                icon: <ExclamationCircleOutlined />,
                content: 'You need to be logged in to file a dispute. Would you like to login now?',
                okText: 'Yes, Login',
                cancelText: 'Cancel',
                onOk: () => {
                    navigate('/login', { state: { from: '/disputes' } });
                }
            });
            return;
        }
        setFileModalVisible(true);
    };


    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            under_review: 'blue',
            awaiting_response: 'purple',
            resolved: 'green',
            closed: 'default',
            cancelled: 'red'
        };
        return colors[status] || 'default';
    };

    const getTypeLabel = (type) => {
        const labels = {
            item_not_received: 'Item Not Received',
            item_not_as_described: 'Item Not As Described',
            damaged_item: 'Damaged Item',
            wrong_item: 'Wrong Item',
            payment_issue: 'Payment Issue',
            refund_request: 'Refund Request',
            other: 'Other'
        };
        return labels[type] || type;
    };

    const columns = [
        {
            title: 'Dispute Number',
            dataIndex: 'dispute_number',
            key: 'dispute_number',
            width: 140,
            fixed: 'left',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            width: 250,
            ellipsis: true,
        },
        {
            title: 'Parties',
            key: 'parties',
            width: 220,
            render: (_, record) => {
                const getFilerName = () => {
                    if (record.filer?.name) return record.filer.name;
                    const firstName = record.filer?.first_name || '';
                    const lastName = record.filer?.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    return fullName || record.filer?.email || 'Unknown';
                };
                
                const getRespondentName = () => {
                    if (record.respondent?.name) return record.respondent.name;
                    const firstName = record.respondent?.first_name || '';
                    const lastName = record.respondent?.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    return fullName || record.respondent?.email || 'Unknown';
                };
                
                const filerName = getFilerName();
                const respondentName = getRespondentName();
                const isCurrentUserFiler = currentUser && record.filer?.id === currentUser.id;
                const isCurrentUserRespondent = currentUser && record.respondent?.id === currentUser.id;
                
                return (
                    <Tooltip 
                        title={`Filed by: ${filerName.includes('@') || filerName === 'Unknown' ? filerName : toTitleCase(filerName)} | Against: ${respondentName.includes('@') || respondentName === 'Unknown' ? respondentName : toTitleCase(respondentName)}`}
                        placement="topLeft"
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Text 
                                strong={isCurrentUserFiler}
                                style={{ 
                                    color: isCurrentUserFiler ? '#1890ff' : '#262626',
                                    fontSize: '13px'
                                }}
                            >
                                {isCurrentUserFiler ? 'You' : (filerName.includes('@') || filerName === 'Unknown' ? filerName : toTitleCase(filerName))}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>vs</Text>
                            <Text 
                                strong={isCurrentUserRespondent}
                                style={{ 
                                    color: isCurrentUserRespondent ? '#1890ff' : '#262626',
                                    fontSize: '13px'
                                }}
                            >
                                {isCurrentUserRespondent ? 'You' : (respondentName.includes('@') || respondentName === 'Unknown' ? respondentName : toTitleCase(respondentName))}
                            </Text>
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 160,
            render: (type) => getTypeLabel(type)
        },
        {
            title: 'Lot',
            key: 'lot',
            width: 250,
            ellipsis: true,
            render: (_, record) => {
                const lotTitle = record.lot?.title || 'N/A';
                const lotNumber = record.lot?.lot_number;
                const truncatedTitle = lotTitle.length > 50 ? `${lotTitle.substring(0, 50)}...` : lotTitle;
                const displayText = lotNumber ? `Lot #${lotNumber}: ${truncatedTitle}` : truncatedTitle;
                
                return (
                    <Tooltip title={lotNumber ? `Lot #${lotNumber}: ${lotTitle}` : lotTitle} placement="topLeft">
                        <Text 
                            ellipsis 
                            style={{ 
                                maxWidth: 300,
                                display: 'block',
                                cursor: 'help'
                            }}
                        >
                            {displayText}
                        </Text>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.replace('_', ' ').toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority) => {
                const colors = {
                    low: 'default',
                    medium: 'blue',
                    high: 'orange',
                    urgent: 'red'
                };
                return (
                    <Tag color={colors[priority]}>
                        {priority.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 130,
            render: (date) => moment(date).format('MMM DD, YYYY')
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => {
                const isFiler = currentUser && record.filer?.id === currentUser.id;
                const canCancel = isFiler && record.status === 'pending';
                
                const menuItems = [
                    {
                        key: 'view',
                        label: 'View Details',
                        icon: <EyeOutlined />,
                        onClick: () => handleViewDispute(record)
                    }
                ];

                if (canCancel) {
                    menuItems.push({
                        key: 'cancel',
                        label: 'Cancel Dispute',
                        icon: <CloseOutlined />,
                        danger: true,
                        onClick: () => handleCancelDispute(record)
                    });
                }

                const menu = (
                    <Menu items={menuItems} />
                );

                return (
                    <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                );
            }
        }
    ];

    // Show login required message if user is not logged in
    useEffect(() => {
        if (!currentUser) {
            const timer = setTimeout(() => {
                Modal.confirm({
                    title: 'Login Required',
                    icon: <ExclamationCircleOutlined />,
                    content: 'You need to be logged in to view and manage disputes. Please login to continue.',
                    okText: 'Yes, Login',
                    cancelText: 'Cancel',
                    onOk: () => {
                        navigate('/login', { state: { from: '/disputes' } });
                    },
                    onCancel: () => {
                        // Don't redirect on cancel, let them see the login required component
                    }
                });
            }, 300);

            return () => clearTimeout(timer);
        }
    }, []);

    if (!currentUser) {
        return (
            <DashboardLayout>
                <LoginRequired
                    title="Login Required"
                    message="You need to be logged in to view and manage disputes. Please login to continue."
                    returnPath="/disputes"
                />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <PageHeader
                title="Disputes"
                description="Manage and track your disputes with sellers and buyers"
                buttonText="File New Dispute"
                buttonIcon={<PlusOutlined />}
                onButtonClick={handleFileDisputeClick}
            />

            {/* Search and Filter Bar */}
            <Card 
                style={{ 
                    marginBottom: 20,
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #f0f0f0',
                    background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)'
                }}
                bodyStyle={{ padding: '20px 24px' }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Space align="center">
                        <FilterOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                        <Typography.Title 
                            level={5} 
                            style={{ margin: 0, fontWeight: 600, color: '#262626' }}
                        >
                            Advanced Search
                        </Typography.Title>
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: '13px', marginLeft: 24 }}>
                        Search by dispute number or subject, and filter by status and type below
                    </Typography.Text>
                </div>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={24} md={10} lg={8} xl={7}>
                        <div style={{ marginBottom: 4 }}>
                            <Typography.Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Search Query
                            </Typography.Text>
                        </div>
                        <Input
                            placeholder="Enter dispute number or subject..."
                            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={handleSearchKeyPress}
                            allowClear
                            size="large"
                            style={{ 
                                borderRadius: 8,
                                border: '1px solid #d9d9d9',
                                transition: 'all 0.3s'
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={12} md={7} lg={6} xl={5}>
                        <div style={{ marginBottom: 4 }}>
                            <Typography.Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Status
                            </Typography.Text>
                        </div>
                        <Select
                            placeholder="All Statuses"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: '100%' }}
                            size="large"
                            suffixIcon={<FilterOutlined style={{ color: '#8c8c8c' }} />}
                            allowClear
                        >
                            <Option value="pending">Pending</Option>
                            <Option value="under_review">Under Review</Option>
                            <Option value="awaiting_response">Awaiting Response</Option>
                            <Option value="resolved">Resolved</Option>
                            <Option value="closed">Closed</Option>
                            <Option value="cancelled">Cancelled</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={12} md={7} lg={6} xl={5}>
                        <div style={{ marginBottom: 4 }}>
                            <Typography.Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Dispute Type
                            </Typography.Text>
                        </div>
                        <Select
                            placeholder="All Types"
                            value={typeFilter}
                            onChange={setTypeFilter}
                            style={{ width: '100%' }}
                            size="large"
                            suffixIcon={<FilterOutlined style={{ color: '#8c8c8c' }} />}
                            allowClear
                        >
                            <Option value="item_not_received">Item Not Received</Option>
                            <Option value="item_not_as_described">Item Not As Described</Option>
                            <Option value="damaged_item">Damaged Item</Option>
                            <Option value="wrong_item">Wrong Item</Option>
                            <Option value="payment_issue">Payment Issue</Option>
                            <Option value="refund_request">Refund Request</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={4} xl={7}>
                        <div style={{ marginBottom: 4, opacity: 0 }}>
                            <Typography.Text strong style={{ fontSize: '13px' }}>
                                Action
                            </Typography.Text>
                        </div>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            size="large"
                            block
                            style={{ 
                                height: 40,
                                borderRadius: 8,
                                fontWeight: 600,
                                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)',
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                border: 'none'
                            }}
                        >
                            Search
                        </Button>
                    </Col>
                </Row>
                {hasActiveFilters && (
                    <div style={{ 
                        marginTop: 16, 
                        paddingTop: 16, 
                        borderTop: '1px solid #f0f0f0',
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 10, 
                        flexWrap: 'wrap' 
                    }}>
                        <Space size="small" wrap>
                            <Text strong style={{ fontSize: '13px', color: '#595959', marginRight: 4 }}>
                                Active Filters:
                            </Text>
                            {activeFilters.search && (
                                <Tag 
                                    closable 
                                    onClose={() => {
                                        setSearchText('');
                                        setActiveFilters(prev => ({ ...prev, search: '' }));
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    color="blue"
                                    style={{ 
                                        borderRadius: 6, 
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    <SearchOutlined style={{ marginRight: 4 }} />
                                    "{activeFilters.search}"
                                </Tag>
                            )}
                            {activeFilters.status && (
                                <Tag 
                                    closable 
                                    onClose={() => {
                                        setStatusFilter(null);
                                        setActiveFilters(prev => ({ ...prev, status: null }));
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    color="geekblue"
                                    style={{ 
                                        borderRadius: 6, 
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Status: {activeFilters.status.replace('_', ' ').charAt(0).toUpperCase() + activeFilters.status.replace('_', ' ').slice(1)}
                                </Tag>
                            )}
                            {activeFilters.type && (
                                <Tag 
                                    closable 
                                    onClose={() => {
                                        setTypeFilter(null);
                                        setActiveFilters(prev => ({ ...prev, type: null }));
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    color="green"
                                    style={{ 
                                        borderRadius: 6, 
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Type: {getTypeLabel(activeFilters.type)}
                                </Tag>
                            )}
                            <Button
                                type="link"
                                size="small"
                                icon={<ClearOutlined />}
                                onClick={handleClearFilters}
                                style={{ 
                                    fontSize: '12px',
                                    padding: 0,
                                    height: 'auto'
                                }}
                            >
                                Clear All
                            </Button>
                        </Space>
                    </div>
                )}
                {hasPendingChanges && (
                    <div style={{ 
                        marginTop: 12, 
                        padding: '10px 14px',
                        background: '#fff7e6',
                        borderRadius: 6,
                        border: '1px solid #ffe58f'
                    }}>
                        <Space>
                            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />
                            <Text type="warning" style={{ fontSize: '13px', fontWeight: 500 }}>
                                Click "Search" to apply your filter changes
                            </Text>
                        </Space>
                    </div>
                )}
            </Card>
          
                <Table
                    columns={columns}
                    dataSource={disputes}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1440 }}
                    pagination={{
                        ...pagination,
                        // showSizeChanger: true,
                        showTotal: (total) => `Total ${total} disputes`,
                        onChange: (page, pageSize) => {
                            setPagination({ ...pagination, current: page, pageSize });
                        }
                    }}
                    locale={{
                        emptyText: currentUser ? (
                            <Empty
                                description="No disputes found"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleFileDisputeClick}
                                >
                                    File Your First Dispute
                                </Button>
                            </Empty>
                        ) : null
                    }}
                />

            <FileDisputeModal
                visible={fileModalVisible}
                onCancel={() => setFileModalVisible(false)}
                onSuccess={() => {
                    setFileModalVisible(false);
                    loadDisputes();
                }}
            />
        </DashboardLayout>
    );
};

export default Disputes;

